import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { XCircle } from "lucide-react";
import { type AffiliateRecord, getAffiliateStatus } from "@/lib/affiliate";
import { useAuth } from "@/lib/auth-context";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AffiliateRegistrationForm } from "./AffiliateRegistrationForm";
import { AffiliateProfileCard } from "./AffiliateProfileCard";
import { AffiliateEditDialog, type SectionId } from "./AffiliateEditDialog";
import { AffiliatePending } from "./AffiliatePending";
import { AffiliateDashboard } from "./AffiliateDashboard";

const VALID_SECTIONS = new Set<SectionId>(["general", "contact", "bank"]);

function AffiliateSkeleton() {
  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Profile card skeleton */}
      <div className="lg:w-72 shrink-0">
        <Card>
          <CardContent className="py-6">
            <div className="flex flex-col items-center gap-4">
              <Skeleton className="size-24 rounded-full" />
              <div className="flex flex-col items-center gap-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-40" />
              </div>
              <Skeleton className="h-6 w-28 rounded-full" />
            </div>
            <div className="my-5 border-t border-border" />
            <div className="flex flex-col gap-3">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-4 w-36" />
            </div>
            <div className="my-5 border-t border-border" />
            <Skeleton className="h-9 w-full rounded-md" />
          </CardContent>
        </Card>
      </div>
      {/* Main content skeleton */}
      <div className="flex-1 min-w-0">
        <Card>
          <CardContent className="py-8">
            <div className="flex flex-col gap-4">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-4 w-full max-w-md" />
              <Skeleton className="h-4 w-full max-w-sm" />
              <div className="mt-4">
                <Skeleton className="h-32 w-full rounded-md" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export function AffiliatePage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [affiliate, setAffiliate] = useState<AffiliateRecord | null>(null);
  const [error, setError] = useState("");

  const tabParam = searchParams.get("tab") as SectionId | null;
  const editOpen = tabParam !== null && VALID_SECTIONS.has(tabParam);
  const editSection: SectionId = editOpen ? tabParam! : "general";

  useEffect(() => {
    getAffiliateStatus()
      .then(setAffiliate)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load status"))
      .finally(() => setLoading(false));
  }, []);

  // Registration form — no page title wrapper needed (form has its own card title)
  if (!loading && !error && !affiliate) {
    return <AffiliateRegistrationForm email={user?.email ?? undefined} onApplied={setAffiliate} />;
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-semibold mb-6">Affiliate</h1>

      {loading && <AffiliateSkeleton />}

      {error && (
        <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>
      )}

      {!loading && !error && affiliate && (
        <>
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Left sidebar — profile card */}
            <div className="lg:w-72 shrink-0">
              <Card>
                <CardContent className="py-6">
                  <AffiliateProfileCard affiliate={affiliate} onEdit={() => setSearchParams({ tab: "general" })} />
                </CardContent>
              </Card>
            </div>

            {/* Center content */}
            <div className="flex-1 min-w-0">
              {affiliate.status === "pending" && <AffiliatePending affiliate={affiliate} />}

              {affiliate.status === "approved" && <AffiliateDashboard affiliate={affiliate} />}

              {affiliate.status === "rejected" && (
                <Card>
                  <CardContent className="flex flex-col items-center gap-4 py-10">
                    <div className="flex size-14 items-center justify-center rounded-full bg-destructive/10">
                      <XCircle className="size-7 text-destructive" />
                    </div>
                    <div className="text-center">
                      <h2 className="text-lg font-semibold">Application Not Approved</h2>
                      <p className="mt-1.5 text-sm text-muted-foreground max-w-sm">
                        Unfortunately your application was not approved. You can update your profile and
                        contact support if you have questions.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Edit dialog */}
          <AffiliateEditDialog
            affiliate={affiliate}
            open={editOpen}
            onOpenChange={(open) => {
              if (!open) setSearchParams({}, { replace: true });
            }}
            onUpdated={setAffiliate}
            initialSection={editSection}
            onSectionChange={(id) => setSearchParams({ tab: id })}
          />
        </>
      )}
    </div>
  );
}
